import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateAirshipRequestSchema,
  CreateMaintenanceWindowRequestSchema,
  toIsoInstant,
  toMoneyCzk,
  type AirshipClass,
  type AirshipStatus,
  type LicenseClass,
  type Role,
} from '@swi/contracts';
import { useState } from 'react';
import { toast } from 'sonner';
import * as api from '../api/endpoints';
import { isApiError } from '../api/problem';
import { PageHeader } from '../components/layout/PageHeader';
import {
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Field,
  Input,
  Modal,
  Pill,
  Select,
  Skeleton,
} from '../components/ui/primitives';
import { useAuth } from '../lib/auth';
import { coords, dateTime, money, toLocalInputValue } from '../lib/format';

/** Fleet, hub and user administration. */
export function AdminPage() {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();

  const airships = useQuery({ queryKey: ['airships', 'admin'], queryFn: () => api.listAirships({ status: undefined }) });
  const hubs = useQuery({ queryKey: ['hubs'], queryFn: api.listHubs });
  const users = useQuery({ queryKey: ['users'], queryFn: api.listUsers });

  const [createOpen, setCreateOpen] = useState(false);
  const [maintenanceFor, setMaintenanceFor] = useState<number | null>(null);

  const refreshFleet = () => {
    void queryClient.invalidateQueries({ queryKey: ['airships'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: AirshipStatus }) =>
      api.updateAirship(id, { status }),
    onSuccess: (airship) => {
      toast.success(`${airship.registration}: ${airship.status}`);
      refreshFleet();
    },
    onError: (error: unknown) =>
      toast.error(isApiError(error) ? error.humanMessage : 'Změna se nepovedla'),
  });

  const setTurnaround = useMutation({
    mutationFn: ({ id, minutes }: { id: number; minutes: number }) =>
      api.updateAirship(id, { turnaroundMinutes: minutes }),
    onSuccess: (airship) => {
      toast.success(`${airship.registration}: turnaround ${String(airship.turnaroundMinutes)} min`);
      refreshFleet();
    },
    onError: (error: unknown) =>
      toast.error(isApiError(error) ? error.humanMessage : 'Změna se nepovedla'),
  });

  const updateUser = useMutation({
    mutationFn: ({
      id,
      role,
      pilotLicenseClass,
    }: {
      id: number;
      role?: Role;
      pilotLicenseClass?: LicenseClass;
    }) => api.updateUser(id, { ...(role ? { role } : {}), ...(pilotLicenseClass ? { pilotLicenseClass } : {}) }),
    onSuccess: () => {
      toast.success('Uživatel upraven');
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['pilots'] });
    },
    onError: (error: unknown) =>
      toast.error(isApiError(error) ? error.humanMessage : 'Změna se nepovedla'),
  });

  return (
    <>
      <PageHeader
        title="Správa"
        description="Flotila, hangáry a uživatelé. Turnaround buffer je per-vzducholoď, protože velká vzducholoď potřebuje delší pozemní obsluhu."
        actions={
          <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
            Přidat vzducholoď
          </Button>
        }
      />

      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader title="Flotila" subtitle="Stav, turnaround a servisní okna" />
          {airships.isLoading ? (
            <Skeleton className="h-48" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[840px] text-[13px]">
                <thead>
                  <tr className="text-left">
                    {['Registrace', 'Vzducholoď', 'Třída', 'Kapacita', 'Sazba', 'Hub', 'Turnaround', 'Stav', ''].map(
                      (head) => (
                        <th key={head} className="label-caps pb-2">
                          {head}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {airships.data?.map((airship) => (
                    <tr key={airship.id} className="border-t border-[var(--border-subtle)]">
                      <td className="mono py-2 text-[var(--accent)]">{airship.registration}</td>
                      <td className="py-2">{airship.name}</td>
                      <td className="py-2">
                        <Pill tone="violet">{airship.airshipClass}</Pill>
                      </td>
                      <td className="mono py-2">{airship.capacityPax}</td>
                      <td className="mono py-2">{money(airship.hourlyRateCzk)}</td>
                      <td className="mono py-2">{airship.homeHub.code}</td>
                      <td className="py-2">
                        <Select
                          value={airship.turnaroundMinutes}
                          onChange={(event) =>
                            setTurnaround.mutate({
                              id: airship.id,
                              minutes: Number(event.target.value),
                            })
                          }
                          className="w-28 py-1 text-[12px]"
                        >
                          {[60, 120, 180, 240, 300, 360, 480, 720].map((minutes) => (
                            <option key={minutes} value={minutes}>
                              {minutes} min
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td className="py-2">
                        <Select
                          value={airship.status}
                          onChange={(event) =>
                            setStatus.mutate({
                              id: airship.id,
                              status: event.target.value as AirshipStatus,
                            })
                          }
                          className="w-36 py-1 text-[12px]"
                        >
                          <option value="ACTIVE">v provozu</option>
                          <option value="MAINTENANCE">v servisu</option>
                          <option value="RETIRED">vyřazena</option>
                        </Select>
                      </td>
                      <td className="py-2">
                        <Button size="sm" variant="outline" onClick={() => setMaintenanceFor(airship.id)}>
                          Servis
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="grid gap-4 xl:grid-cols-2">
          <Card>
            <CardHeader title="Hangáry" subtitle="Huby, ze kterých flotila operuje" />
            {hubs.isLoading ? (
              <Skeleton className="h-40" />
            ) : (
              <ul className="flex flex-col gap-2">
                {hubs.data?.map((hub) => (
                  <li
                    key={hub.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius)] border border-[var(--border-subtle)] px-3 py-2"
                  >
                    <span>
                      <span className="mono text-[13px] font-semibold text-[var(--accent)]">
                        {hub.code}
                      </span>
                      <span className="ml-2 text-[13px]">{hub.name}</span>
                      <span className="block text-[11px] text-[var(--text-muted)]">
                        {hub.city}, {hub.country} · {hub.timezone}
                      </span>
                    </span>
                    <span className="mono text-[11px] text-[var(--text-secondary)]">
                      {coords(hub.latitude, hub.longitude)} · {hub.hangarCapacity} míst
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Uživatelé"
              subtitle={
                hasRole('ADMIN')
                  ? 'Role a licenční třídy pilotů'
                  : 'Úpravy může dělat jen ADMIN'
              }
            />
            {users.isLoading ? (
              <Skeleton className="h-40" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[460px] text-[13px]">
                  <thead>
                    <tr className="text-left">
                      {['Jméno', 'E-mail', 'Role', 'Licence'].map((head) => (
                        <th key={head} className="label-caps pb-2">
                          {head}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.data?.map((user) => (
                      <tr key={user.id} className="border-t border-[var(--border-subtle)]">
                        <td className="py-2">{user.displayName}</td>
                        <td className="mono py-2 text-[11px] text-[var(--text-secondary)]">
                          {user.email}
                        </td>
                        <td className="py-2">
                          {hasRole('ADMIN') ? (
                            <Select
                              value={user.role}
                              onChange={(event) =>
                                updateUser.mutate({ id: user.id, role: event.target.value as Role })
                              }
                              className="w-32 py-1 text-[12px]"
                            >
                              <option value="RENTER">RENTER</option>
                              <option value="OWNER_STAFF">OWNER_STAFF</option>
                              <option value="ADMIN">ADMIN</option>
                            </Select>
                          ) : (
                            <Pill tone="neutral">{user.role}</Pill>
                          )}
                        </td>
                        <td className="py-2">
                          {hasRole('ADMIN') ? (
                            <Select
                              value={user.pilotLicenseClass}
                              onChange={(event) =>
                                updateUser.mutate({
                                  id: user.id,
                                  pilotLicenseClass: event.target.value as LicenseClass,
                                })
                              }
                              className="w-24 py-1 text-[12px]"
                            >
                              <option value="NONE">—</option>
                              <option value="A">A</option>
                              <option value="B">B</option>
                              <option value="C">C</option>
                            </Select>
                          ) : (
                            <Pill tone={user.pilotLicenseClass === 'NONE' ? 'neutral' : 'accent'}>
                              {user.pilotLicenseClass}
                            </Pill>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>

      <CreateAirshipModal
        open={createOpen}
        hubs={hubs.data ?? []}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          refreshFleet();
        }}
      />

      <MaintenanceModal
        airshipId={maintenanceFor}
        onClose={() => setMaintenanceFor(null)}
        onCreated={() => {
          setMaintenanceFor(null);
          refreshFleet();
        }}
      />
    </>
  );
}

function CreateAirshipModal({
  open,
  hubs,
  onClose,
  onCreated,
}: {
  open: boolean;
  hubs: Array<{ id: number; code: string; name: string }>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [registration, setRegistration] = useState('');
  const [name, setName] = useState('');
  const [model, setModel] = useState('');
  const [airshipClass, setAirshipClass] = useState<AirshipClass>('B');
  const [capacityPax, setCapacityPax] = useState(12);
  const [cruiseSpeedKts, setCruiseSpeedKts] = useState(40);
  const [maxWindSpeedKts, setMaxWindSpeedKts] = useState(28);
  const [rate, setRate] = useState(12000);
  const [turnaround, setTurnaround] = useState(240);
  const [homeHubId, setHomeHubId] = useState<number | null>(null);
  const [issues, setIssues] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: api.createAirship,
    onSuccess: (airship) => {
      toast.success(`${airship.registration} přidána do flotily`);
      onCreated();
    },
    onError: (error: unknown) => {
      setIssues(isApiError(error) ? error.humanMessage : 'Vytvoření se nepovedlo');
    },
  });

  const submit = () => {
    const parsed = CreateAirshipRequestSchema.safeParse({
      registration: registration.toUpperCase(),
      name,
      model,
      airshipClass,
      capacityPax,
      cruiseSpeedKts,
      maxWindSpeedKts,
      hourlyRateCzk: toMoneyCzk(rate),
      homeHubId: homeHubId ?? hubs[0]?.id ?? 0,
      turnaroundMinutes: turnaround,
    });
    if (!parsed.success) {
      setIssues(
        parsed.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`).join(' · '),
      );
      return;
    }
    setIssues(null);
    create.mutate(parsed.data);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nová vzducholoď"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Zrušit
          </Button>
          <Button variant="primary" loading={create.isPending} onClick={submit}>
            Přidat
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Registrace" hint="Velká písmena, číslice a pomlčky">
          <Input
            value={registration}
            onChange={(event) => setRegistration(event.target.value.toUpperCase())}
            placeholder="OK-ZEP-09"
          />
        </Field>
        <Field label="Název">
          <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Aurora II" />
        </Field>
        <Field label="Model">
          <Input value={model} onChange={(event) => setModel(event.target.value)} placeholder="Zeppelin NT-14" />
        </Field>
        <Field label="Třída">
          <Select
            value={airshipClass}
            onChange={(event) => setAirshipClass(event.target.value as AirshipClass)}
          >
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
          </Select>
        </Field>
        <Field label="Kapacita (pax)">
          <Input
            type="number"
            min={1}
            value={capacityPax}
            onChange={(event) => setCapacityPax(Number(event.target.value))}
          />
        </Field>
        <Field label="Cestovní rychlost (kts)">
          <Input
            type="number"
            min={1}
            value={cruiseSpeedKts}
            onChange={(event) => setCruiseSpeedKts(Number(event.target.value))}
          />
        </Field>
        <Field label="Max. vítr (kts)">
          <Input
            type="number"
            min={1}
            value={maxWindSpeedKts}
            onChange={(event) => setMaxWindSpeedKts(Number(event.target.value))}
          />
        </Field>
        <Field label="Sazba (CZK / h)">
          <Input
            type="number"
            min={0}
            step={500}
            value={rate}
            onChange={(event) => setRate(Number(event.target.value))}
          />
        </Field>
        <Field label="Turnaround (min)" hint="Povinná pozemní obsluha mezi lety">
          <Input
            type="number"
            min={0}
            step={30}
            value={turnaround}
            onChange={(event) => setTurnaround(Number(event.target.value))}
          />
        </Field>
        <Field label="Domovský hub">
          <Select
            value={homeHubId ?? hubs[0]?.id ?? ''}
            onChange={(event) => setHomeHubId(Number(event.target.value))}
          >
            {hubs.map((hub) => (
              <option key={hub.id} value={hub.id}>
                {hub.code} — {hub.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {issues !== null && (
        <div className="mt-3">
          <ErrorNote title="Formulář nelze odeslat" detail={issues} />
        </div>
      )}
    </Modal>
  );
}

function MaintenanceModal({
  airshipId,
  onClose,
  onCreated,
}: {
  airshipId: number | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [startLocal, setStartLocal] = useState(toLocalInputValue(new Date(Date.now() + 86_400_000)));
  const [endLocal, setEndLocal] = useState(toLocalInputValue(new Date(Date.now() + 3 * 86_400_000)));
  const [reason, setReason] = useState('Pravidelná prohlídka obalu');
  const [issues, setIssues] = useState<string | null>(null);

  const existing = useQuery({
    queryKey: ['airship', airshipId, 'maintenance'],
    queryFn: () => api.listMaintenance(airshipId!),
    enabled: airshipId !== null,
  });

  const create = useMutation({
    mutationFn: () => {
      const parsed = CreateMaintenanceWindowRequestSchema.safeParse({
        startAt: toIsoInstant(new Date(startLocal)),
        endAt: toIsoInstant(new Date(endLocal)),
        reason,
      });
      if (!parsed.success) {
        throw new Error(
          parsed.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`).join(' · '),
        );
      }
      return api.addMaintenance(airshipId!, parsed.data);
    },
    onSuccess: () => {
      toast.success('Servisní okno naplánováno');
      onCreated();
    },
    onError: (error: unknown) => {
      setIssues(
        isApiError(error)
          ? error.humanMessage
          : error instanceof Error
            ? error.message
            : 'Nepovedlo se',
      );
    },
  });

  return (
    <Modal
      open={airshipId !== null}
      onClose={onClose}
      title="Servisní okno"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Zavřít
          </Button>
          <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
            Naplánovat
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Od">
          <Input
            type="datetime-local"
            value={startLocal}
            onChange={(event) => setStartLocal(event.target.value)}
          />
        </Field>
        <Field label="Do">
          <Input
            type="datetime-local"
            value={endLocal}
            onChange={(event) => setEndLocal(event.target.value)}
          />
        </Field>
        <Field label="Důvod" className="sm:col-span-2">
          <Input value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
      </div>

      {(existing.data?.length ?? 0) > 0 && (
        <div className="mt-4">
          <p className="label-caps mb-2">Už naplánováno</p>
          <ul className="flex flex-col gap-1.5">
            {existing.data?.map((window) => (
              <li key={window.id} className="mono text-[11px] text-[var(--text-secondary)]">
                {dateTime(window.startAt)} → {dateTime(window.endAt)} · {window.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {issues !== null && (
        <div className="mt-3">
          <ErrorNote title="Servis nelze naplánovat" detail={issues} />
        </div>
      )}
    </Modal>
  );
}
